/**
 * Maaş Hesaplama Çekirdeği — Faz 5.6: İzin hakkı ödemesi (build-up modeli).
 *
 * Faz 5.5: Manuel maaş hareketleri.
 *
 * Faz 5.6 — Market + aylık personel için "build-up" maaş modeli:
 *  - Taban artık `ucret` değil, GERÇEK çalışılan saat × saatlik karşılık.
 *  - Üstüne aylık izin hakkı ödemesi: izinHakki × 10 × saatlik
 *    (izinHakki = min(4, round(mesaiGunu / 6.5)); kullanılsa da kullanılmasa da tamamı).
 *  - Üstüne yıllık izin ödemesi: o ay kullanılan yıllık izin günü × 10 × saatlik
 *    (yıllık izin ödenekli; build-up'ta çalışılmayan gün ödenmediği için elle eklenir).
 *  - Eksik mesai kesintisi + "maaştan kesilsin" izin kesintisi KALDIRILDI
 *    (çalışılan saat zaten azını ödüyor; o günler çalışılmadığı için zaten 0).
 *  - Standart ayda (26 mesai + 4 izin) sonuç yine tam ucret çıkar.
 *  - SADECE takvimTipi='market' + maasTipi='aylik'. Ofis (sabit ucret) ve saatlik DEĞİŞMEZ.
 *  - netMaas == anlikNet (canlı ekran ile ay sonu birebir aynı, canlı sayaç korunur).
 */

import type { KullaniciPersonel } from '@/types'
import {
  collection, getDocs, query, where, Timestamp,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import type { Kayit } from '@/lib/services/kayit-service'
import type { Mola } from '@/lib/services/mola-service'
import { ayTumPuantajlar, type Puantaj, puantajToplam } from '@/lib/services/puantaj-service'
import { personelAvanslari, type Avans } from '@/lib/services/avans-service'
import { izinleriGetir, maasKesilen, yillikKesilen, type IzinTalebi } from '@/lib/services/izin-service'
import {
  yilTatilleriGetir, type ResmiTatil, type TatilKatsayisi,
} from '@/lib/services/tatil-service'
import {
  katsayiGetir, buYilDogumGunu,
  type DogumKatsayisi,
} from '@/lib/services/dogum-service'
import {
  ayVardiyaKontrolu,
  type VardiyaKontrolSonuc,
} from '@/lib/services/vardiya-kontrol-service'
import { ayarlariGetir } from '@/lib/services/ayarlar-service'
import {
  personelHareketleriniGetir, hareketToplam, hareketGecerli,
  type MaasHareketi,
} from '@/lib/services/maas-hareket-service'
import { personelOnayliExtralar } from '@/lib/services/extra-mesai-service'
import {
  mesaiAraliklariniHesapla, tavanliMesaiHesabi,
} from './calisma-hesabi'
import { AYLIK_STANDART_SAAT, MOLA_HAKKI_DK } from './sabitler'

export const IZIN_GUN_SAATI = 10
export const BONUS_GUNLUK_CAP_DK = 600

/** Build-up izin hakkı: kaç çalışma günü = 1 izin günü (ortalama 6.5). */
export const IZIN_HAKKI_GUN_BOLEN = 6.5
/** Aylık maksimum izin hakkı günü. */
export const IZIN_HAKKI_MAX = 4

export interface TatilCalisma {
  tarih: string
  tatilAdi: string
  katsayi: TatilKatsayisi
  efektifKatsayi: number
  dakika: number
  bonusDakika: number
  bonus: number
}

export interface DogumGunuCalisma {
  tarih: string
  katsayi: DogumKatsayisi
  dakika: number
  bonusDakika: number
  bonus: number
}

export interface MaasHesabi {
  personelId: string
  donem: string

  toplamDakika: number
  toplamSaat: number
  saatlikKarsilik: number

  /** Tavanı aşan, ÖDENMEYEN fazla mesai dakikası (yetkisiz personel — gizli) */
  fazlaMesaiDakika: number
  /** Tavana kadar olan normal mesai dakikası */
  normalMesaiDakika: number
  /** Yetkili personelin ÖDENEN ek mesai dakikası */
  ekMesaiDakika: number
  /** Şu an açık aralık tavanı aşmış mı (yetkisiz — canlı sayaç durması için) */
  acikAralikTavanAsti: boolean
  /** Yetkili + açık aralık tavanı aştı → canlı sayaç ek mesaiye aksın */
  acikAralikEkMesaide: boolean

  molaDakikaToplam: number
  molaAsanDakika: number
  molaAsanKesinti: number

  bazUcret: number
  anlikBaz: number

  /** Build-up maaş modeli aktif mi (market + aylık) */
  buildUp: boolean
  /** O ay çalışmaya gelinen gün sayısı (build-up izin hakkı için) */
  mesaiGunu: number
  /** Hak edilen aylık izin günü (build-up: min(4, round(mesaiGunu/6.5))) */
  izinHakki: number
  /** İzin hakkı ödemesi (izinHakki × 10 × saatlik) */
  izinAlacagi: number
  /** O ay kullanılan yıllık izin günü */
  yillikIzinGun: number
  /** Yıllık izin ödemesi (yillikIzinGun × 10 × saatlik) */
  yillikIzinOdemesi: number

  puantajToplam: number
  odenmisAvansToplam: number
  sabitEkOdeme: number
  sabitKesinti: number

  izinMaasGunToplam: number
  izinMaasKesinti: number

  tatilBonusToplam: number
  tatilCalismalari: TatilCalisma[]

  dogumGunuBonus: number
  dogumGunuCalisma: DogumGunuCalisma | null

  vardiyaKontrolSonuclari: VardiyaKontrolSonuc[]
  gecGelmeSayisi: number
  erkenCikmaSayisi: number
  noShowSayisi: number
  eksikMesaiDakika: number
  eksikMesaiKesinti: number
  gecGelmeCezasi: number
  gecCezaBirimTL: number
  noShowCezasi: number
  noShowCezaBirimTL: number

  // Manuel hareketler (kasa eksiği, hasar vb.)
  manuelHareketler: MaasHareketi[]
  manuelEklemeToplam: number    // pozitif toplam
  manuelKesintiToplam: number   // pozitif toplam (gösterim için)
  manuelHareketNet: number      // ekleme - kesinti

  netMaas: number
  anlikNet: number

  puantajlar: Puantaj[]
  avanslar: Avans[]
  maasKesilenIzinler: IzinTalebi[]
}

export function saatlikKarsilik(personel: KullaniciPersonel): number {
  if (personel.maasTipi === 'saatlik') return personel.ucret
  return personel.ucret / AYLIK_STANDART_SAAT
}

function efektifKatsayi(k: TatilKatsayisi): number {
  if (k === 'calismaz') return 2
  return k
}

function lokalIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function donemAraligi(donem: string): { baslangic: Timestamp; bitis: Timestamp } {
  const [y, m] = donem.split('-').map(Number)
  const baslangic = new Date(y!, m! - 1, 1, 0, 0, 0, 0)
  const bitis = new Date(y!, m!, 1, 0, 0, 0, 0)
  return {
    baslangic: Timestamp.fromDate(baslangic),
    bitis: Timestamp.fromDate(bitis),
  }
}

async function donemKayitlariniGetir(personelId: string, donem: string): Promise<Kayit[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, 'kayitlar'), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Kayit)
  const { baslangic, bitis } = donemAraligi(donem)
  return liste.filter((k) => {
    const ms = k.zaman.toMillis()
    return ms >= baslangic.toMillis() && ms < bitis.toMillis()
  })
}

async function donemMolalariniGetir(personelId: string, donem: string): Promise<Mola[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, 'molalar'), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Mola)
  const { baslangic, bitis } = donemAraligi(donem)
  return liste.filter((m) => {
    const ms = m.baslangic.toMillis()
    return ms >= baslangic.toMillis() && ms < bitis.toMillis()
  })
}

function molaToplamDk(molalar: Mola[]): number {
  let toplamMs = 0
  for (const m of molalar) {
    if (!m.bitis) continue
    toplamMs += m.bitis.toMillis() - m.baslangic.toMillis()
  }
  return Math.floor(toplamMs / 60000)
}

function odenmisAvansToplami(avanslar: Avans[], donem: string): number {
  return avanslar
    .filter((a) => a.ay === donem && a.durum === 'odendi')
    .reduce((acc, a) => acc + (a.onayTutari ?? a.talepTutari), 0)
}

/**
 * O ayki izin dağılımı:
 *  - maasGun:   "maaştan kesilsin" izin günleri (build-up DIŞI personel için kesinti)
 *  - yillikGun: yıllık (ödenekli) izin günleri (build-up'ta ÖDEME olarak eklenir)
 */
async function donemIzinMaasKesintisi(
  personelId: string,
  donem: string
): Promise<{ maasGun: number; yillikGun: number; izinler: IzinTalebi[] }> {
  const tumOnayli = await izinleriGetir({
    personelId,
    durumlar: ['onaylandi'],
  })
  const [y, m] = donem.split('-')
  const ayPrefix = `${y}-${m}-`
  const buAyIzinler = tumOnayli.filter(
    (i) => i.baslangicTarihi.startsWith(ayPrefix)
  )
  const maasGun = buAyIzinler.reduce((acc, i) => acc + maasKesilen(i), 0)
  const yillikGun = buAyIzinler.reduce((acc, i) => acc + yillikKesilen(i), 0)
  return { maasGun, yillikGun, izinler: buAyIzinler.filter((i) => maasKesilen(i) > 0) }
}

function gunlukDakikaHaritasi(kayitlar: Kayit[]): Map<string, number> {
  const harita = new Map<string, number>()
  const araliklar = mesaiAraliklariniHesapla(kayitlar)
  for (const a of araliklar) {
    if (a.dakika <= 0) continue
    const baslangic = a.giris.zaman.toDate()
    const bitis = a.cikis
      ? a.cikis.zaman.toDate()
      : new Date(baslangic.getTime() + a.dakika * 60000)
    let cursor = new Date(baslangic)
    while (cursor < bitis) {
      const gunSonu = new Date(cursor)
      gunSonu.setHours(24, 0, 0, 0)
      const segmentBitis = gunSonu < bitis ? gunSonu : bitis
      const segmentDk = Math.floor(
        (segmentBitis.getTime() - cursor.getTime()) / 60000
      )
      if (segmentDk > 0) {
        const iso = lokalIso(cursor)
        harita.set(iso, (harita.get(iso) ?? 0) + segmentDk)
      }
      cursor = gunSonu
    }
  }
  return harita
}

/**
 * Çalışmaya gelinen gün sayısı (build-up izin hakkı için).
 * Her giriş-çıkış aralığının GİRİŞ günü 1 gün sayılır (gece vardiyası tek gün).
 * 9 saatlik bir gün de 1 gün sayılır (kullanıcı kararı).
 */
function mesaiGunSayisi(kayitlar: Kayit[]): number {
  const gunler = new Set<string>()
  for (const a of mesaiAraliklariniHesapla(kayitlar)) {
    if (a.dakika > 0) gunler.add(lokalIso(a.giris.zaman.toDate()))
  }
  return gunler.size
}

async function tatilBonusunuHesapla(
  donem: string,
  gunlukDakika: Map<string, number>,
  saatlik: number
): Promise<{ bonus: number; detay: TatilCalisma[] }> {
  const yil = Number(donem.split('-')[0])
  const [buYilTatilleri, oncekiYilTatilleri] = await Promise.all([
    yilTatilleriGetir(yil),
    yilTatilleriGetir(yil - 1),
  ])
  const tumTatiller = [...buYilTatilleri, ...oncekiYilTatilleri]
  const tatilMap = new Map<string, ResmiTatil>()
  tumTatiller.forEach((t) => tatilMap.set(t.tarih, t))

  const detay: TatilCalisma[] = []
  let bonus = 0
  for (const [iso, dakika] of gunlukDakika) {
    const tatil = tatilMap.get(iso)
    if (!tatil) continue
    const efektif = efektifKatsayi(tatil.katsayi)
    const bonusDakika = Math.min(dakika, BONUS_GUNLUK_CAP_DK)
    const bonusGun = (bonusDakika / 60) * saatlik * (efektif - 1)
    bonus += bonusGun
    detay.push({
      tarih: iso,
      tatilAdi: tatil.isim,
      katsayi: tatil.katsayi,
      efektifKatsayi: efektif,
      dakika,
      bonusDakika,
      bonus: bonusGun,
    })
  }
  detay.sort((a, b) => a.tarih.localeCompare(b.tarih))
  return { bonus, detay }
}

async function dogumGunuBonusunuHesapla(
  personel: KullaniciPersonel,
  donem: string,
  gunlukDakika: Map<string, number>,
  saatlik: number
): Promise<{ bonus: number; calisma: DogumGunuCalisma | null }> {
  if (!personel.dogumTarihi) return { bonus: 0, calisma: null }
  const yil = Number(donem.split('-')[0])
  const ay = donem.split('-')[1]
  const dogumIso = buYilDogumGunu(personel.dogumTarihi, yil)
  if (!dogumIso.startsWith(`${yil}-${ay}-`)) return { bonus: 0, calisma: null }
  const katsayiKaydi = await katsayiGetir(personel.id, yil)
  if (!katsayiKaydi) return { bonus: 0, calisma: null }
  const dakika = gunlukDakika.get(dogumIso) ?? 0
  const bonusDakika = Math.min(dakika, BONUS_GUNLUK_CAP_DK)
  const bonus = (bonusDakika / 60) * saatlik * (katsayiKaydi.katsayi - 1)
  return {
    bonus,
    calisma: { tarih: dogumIso, katsayi: katsayiKaydi.katsayi, dakika, bonusDakika, bonus },
  }
}

export async function maasHesapla(
  personel: KullaniciPersonel,
  donem: string
): Promise<MaasHesabi> {
  const [kayitlar, molalar, puantajlar, avanslar, izinKesinti, vardiyaKontrol, ayarlar, hareketler, onayliExtralar] = await Promise.all([
    donemKayitlariniGetir(personel.id, donem),
    donemMolalariniGetir(personel.id, donem),
    ayTumPuantajlar(donem).then((tum) =>
      tum.filter((p) => p.personelId === personel.id)
    ),
    personelAvanslari(personel.id),
    donemIzinMaasKesintisi(personel.id, donem),
    ayVardiyaKontrolu(personel.id, donem),
    ayarlariGetir(),
    personelHareketleriniGetir(personel.id, donem),
    personelOnayliExtralar(personel.id, donem),
  ])

  // Build-up maaş modeli: sadece market + aylık personel.
  // (Ofis aylık = sabit ucret, saatlik = saatine göre — ikisi de eski mantıkta kalır.)
  const buildUp = personel.takvimTipi === 'market' && personel.maasTipi === 'aylik'

  // Mesai tavanı (KARAR #11/#13): vardiya bitişini aşan süre, ancak yetkili
  // personelin extra'sı ONAYLANMIŞSA ve onaylanan süre kadar ödenir.
  // Eski (deploy öncesi) onaylı taleplerde süre yoksa varsayılan tavan uygulanır.
  const donemVardiyalari = vardiyaKontrol.sonuclar.map((s) => s.vardiya)
  const VARSAYILAN_EK_DK = 120  // eski onaylı talepler için (onayliEkDakika yoksa)
  const onayliExtraMap = new Map<string, number>()
  onayliExtralar.forEach((t) => {
    if (t.girisKayitId) {
      onayliExtraMap.set(t.girisKayitId, t.onayliEkDakika ?? VARSAYILAN_EK_DK)
    }
  })
  const tavanli = tavanliMesaiHesabi(
    kayitlar,
    donemVardiyalari,
    personel.extraMesaiYetkisi === true,
    onayliExtraMap
  )
  const normalMesaiDakika = tavanli.normalDakika
  const ekMesaiDakika = tavanli.ekMesaiDakika
  const fazlaMesaiDakika = tavanli.odenmeyenFazlaDakika
  const acikAralikTavanAsti = tavanli.acikAralikTavanAsti
  const acikAralikEkMesaide = tavanli.acikAralikEkMesaide
  // Ödenen toplam = normal + ek mesai (ödenmeyen fazla dahil değil)
  const toplamDakika = normalMesaiDakika + ekMesaiDakika
  const toplamSaat = toplamDakika / 60
  const saatlik = saatlikKarsilik(personel)

  const molaDakikaToplam = molaToplamDk(molalar)
  const molaAsanDakika = Math.max(0, molaDakikaToplam - MOLA_HAKKI_DK)
  const molaAsanKesinti = (molaAsanDakika / 60) * saatlik

  // ─── İzin hesapları ─────────────────────────────────────────────
  // Build-up DIŞI personel: "maaştan kesilsin" izin günleri maaştan düşer (eski mantık).
  // Build-up personel: o günler çalışılmadığı için zaten 0 ödeniyor → kesinti SIFIR.
  const izinMaasGunToplam = izinKesinti.maasGun
  const izinMaasKesinti = buildUp ? 0 : izinMaasGunToplam * IZIN_GUN_SAATI * saatlik

  // Build-up: aylık izin hakkı ödemesi (kullanılsa da kullanılmasa da tamamı ödenir).
  const mesaiGunu = mesaiGunSayisi(kayitlar)
  const izinHakki = buildUp
    ? Math.min(IZIN_HAKKI_MAX, Math.round(mesaiGunu / IZIN_HAKKI_GUN_BOLEN))
    : 0
  const izinAlacagi = izinHakki * IZIN_GUN_SAATI * saatlik

  // Build-up: yıllık (ödenekli) izin ödemesi (çalışılmayan ama ödenen günler).
  const yillikIzinGun = buildUp ? izinKesinti.yillikGun : 0
  const yillikIzinOdemesi = yillikIzinGun * IZIN_GUN_SAATI * saatlik

  const gunlukDakika = gunlukDakikaHaritasi(kayitlar)

  const [{ bonus: tatilBonusToplam, detay: tatilCalismalari }, dogumGunu] = await Promise.all([
    tatilBonusunuHesapla(donem, gunlukDakika, saatlik),
    dogumGunuBonusunuHesapla(personel, donem, gunlukDakika, saatlik),
  ])

  // Eksik mesai kesintisi: SADECE build-up DIŞI aylık personel için.
  // Build-up'ta çalışılan saat zaten azını ödediği için ikinci kez kesilmez.
  const eksikMesaiDakika = vardiyaKontrol.toplamEksikDk
  const eksikMesaiKesinti = (personel.maasTipi === 'aylik' && !buildUp)
    ? (eksikMesaiDakika / 60) * saatlik
    : 0

  const gecCezaBirimTL = ayarlar.puantajBossTutari
  const gecCezaSayisi = Math.max(0, vardiyaKontrol.gecGelmeSayisi - 2)
  const gecGelmeCezasi = gecCezaSayisi * gecCezaBirimTL

  const noShowCezaBirimTL = IZIN_GUN_SAATI * saatlik
  const noShowCezasi = vardiyaKontrol.noShowSayisi * noShowCezaBirimTL

  // Manuel hareketler — maaşa SADECE onaylı (veya eski/durumsuz) hareketler yansır.
  // Yönetici eklediği 'bekliyor' hareketler BOSS onaylayana kadar nete katılmaz.
  const gecerliHareketler = hareketler.filter(hareketGecerli)
  const manuelEklemeToplam = gecerliHareketler
    .filter((h) => h.tip === 'ekleme')
    .reduce((acc, h) => acc + h.tutar, 0)
  const manuelKesintiToplam = gecerliHareketler
    .filter((h) => h.tip === 'kesinti')
    .reduce((acc, h) => acc + h.tutar, 0)
  const manuelHareketNet = hareketToplam(hareketler)  // hareketToplam zaten onaylı filtreler

  const anlikBaz = toplamSaat * saatlik
  // Build-up + saatlik: taban = gerçek çalışılan saat. Ofis aylık: sabit ucret.
  const bazUcret =
    (personel.maasTipi === 'saatlik' || buildUp)
      ? anlikBaz
      : personel.ucret

  const puantajT = puantajToplam(puantajlar)
  const odenmisAvans = odenmisAvansToplami(avanslar, donem)
  const sabitEk = personel.sabitEkOdeme ?? 0
  const sabitKes = personel.sabitKesinti ?? 0

  const ekKesintiler =
    -molaAsanKesinti
    - izinMaasKesinti
    + izinAlacagi          // build-up: aylık izin hakkı ödemesi (yoksa 0)
    + yillikIzinOdemesi    // build-up: yıllık izin ödemesi (yoksa 0)
    + tatilBonusToplam
    + dogumGunu.bonus
    + puantajT
    - odenmisAvans
    + sabitEk
    - sabitKes
    - eksikMesaiKesinti
    - gecGelmeCezasi
    - noShowCezasi
    + manuelHareketNet

  const netMaas = bazUcret + ekKesintiler
  const anlikNet = anlikBaz + ekKesintiler

  return {
    personelId: personel.id,
    donem,
    toplamDakika,
    toplamSaat,
    saatlikKarsilik: saatlik,
    fazlaMesaiDakika,
    normalMesaiDakika,
    ekMesaiDakika,
    acikAralikTavanAsti,
    acikAralikEkMesaide,
    molaDakikaToplam,
    molaAsanDakika,
    molaAsanKesinti,
    bazUcret,
    anlikBaz,
    buildUp,
    mesaiGunu,
    izinHakki,
    izinAlacagi,
    yillikIzinGun,
    yillikIzinOdemesi,
    puantajToplam: puantajT,
    odenmisAvansToplam: odenmisAvans,
    sabitEkOdeme: sabitEk,
    sabitKesinti: sabitKes,
    izinMaasGunToplam,
    izinMaasKesinti,
    tatilBonusToplam,
    tatilCalismalari,
    dogumGunuBonus: dogumGunu.bonus,
    dogumGunuCalisma: dogumGunu.calisma,
    vardiyaKontrolSonuclari: vardiyaKontrol.sonuclar,
    gecGelmeSayisi: vardiyaKontrol.gecGelmeSayisi,
    erkenCikmaSayisi: vardiyaKontrol.erkenCikmaSayisi,
    noShowSayisi: vardiyaKontrol.noShowSayisi,
    eksikMesaiDakika,
    eksikMesaiKesinti,
    gecGelmeCezasi,
    gecCezaBirimTL,
    noShowCezasi,
    noShowCezaBirimTL,
    manuelHareketler: hareketler,
    manuelEklemeToplam,
    manuelKesintiToplam,
    manuelHareketNet,
    netMaas,
    anlikNet,
    puantajlar,
    avanslar: avanslar.filter((a) => a.ay === donem),
    maasKesilenIzinler: izinKesinti.izinler,
  }
}

export async function buAyMaasHesapla(personel: KullaniciPersonel): Promise<MaasHesabi> {
  const d = new Date()
  const donem = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  return maasHesapla(personel, donem)
}
